import React, { useEffect, useState } from 'react';
import './style.scss';
import { TabContent, TabPane } from 'reactstrap';
import { TabView } from './models/tabView';
import NavigationPanelContainer from './navigationPanelContainer';
import { IContextMenuItem } from 'tacho-ui';
import { generateGuid } from '../../../core/helpers/guid';
import { usePrevious } from '../../hooks/usePrevious';

interface TabNavigatorContainerProps {
    tabs: Array<TabView>;
    renderOnlyActiveTab: boolean;
    upperCaseTabs: boolean;
    className?: string;
    tabContentClassName?: string;
    tabPaneClassName?: string;
    parentWidth?: number;
    isTabChangeBlocked?: boolean;
    stayOnTab?: boolean;
    suppressDropdown?: boolean;
    initialTab?: number;
    customContextMenuItems?: Array<IContextMenuItem>;
    showContextMenu?: boolean;
    redirectToTabAfterClose?: number;
    inModal?: boolean;
    closeTabsHandler?: () => void;
    tabChanged?: (newTabIndex: number) => void;
    onTabClose?: (closedTab: TabView) => void;
    tabClicked?: (tabIndex: number | undefined) => void;
    setRedirectTo?: (tabIndex: number | undefined) => void;
}

export function TabsNavigator(props: TabNavigatorContainerProps) {
    const tabsNavigationDOMId = 'tabsNavigationDOMId' + generateGuid().toString().replace(/-/gi, '');

    const [activeTab, setActiveTab] = useState(props.initialTab || 0);
    const [previousTab, setPreviousTab] = useState(props.initialTab || 0);
    const [ClickedTab, setClickedTab] = useState(props.initialTab || 0);
    const [lastClickedTab, setLAstClickedTab] = useState(props.initialTab || 0);

    const prevProps = usePrevious(props);

    useEffect(() => {
        if (props.redirectToTabAfterClose !== undefined) {
            goToTab(props.redirectToTabAfterClose);
            props.setRedirectTo?.(undefined);
        }
    }, [props.redirectToTabAfterClose]);

    useEffect(() => {
        if (prevProps && prevProps.tabs.length < props.tabs.length) {
            goToTab(props.tabs.length - 1);
        } else if (prevProps && prevProps.tabs.length > props.tabs.length && activeTab >= props.tabs.length) {
            goToTab(previousTab);
        }
    }, [props.tabs]);

    useEffect(() => {
        goToTab(ClickedTab);
    }, [props.isTabChangeBlocked]);

    useEffect(() => {
        if (props.stayOnTab) {
            setClickedTab(activeTab);
        }
        props.tabClicked && props.tabClicked(undefined);
    }, [ClickedTab]);

    useEffect(() => {
        props.stayOnTab ? setClickedTab(activeTab) : setClickedTab(lastClickedTab);
    }, [props.stayOnTab]);

    const goToTab = (tabIndex: number): void => {
        setClickedTab(tabIndex);
        setLAstClickedTab(tabIndex);
        if (tabIndex !== activeTab) {
            props.tabClicked && props.tabClicked(tabIndex);
        }

        let tabToGo = tabIndex;

        if (tabIndex >= props.tabs.length) {
            tabToGo = props.tabs.length - 1;
        }
        if (props.isTabChangeBlocked !== true) {
            setPreviousTab(activeTab);
            setActiveTab(tabToGo);
        }
        props.tabChanged && props.tabChanged(tabToGo);
    };

    const onTabClose = (tabIndex: number): void => {
        if (tabIndex >= props.tabs.length) {
            console.log('Cannot remove tab which index is exeeding actual tabs array.');
            return;
        }

        if (tabIndex === activeTab) {
            goToTab(previousTab);
        } else if (tabIndex < activeTab) {
            goToTab(activeTab - 1);
        }

        if (props.renderOnlyActiveTab) {
            setActiveTab(previousTab);
        }

        if (props.onTabClose) {
            const tabToRemove = props.tabs[tabIndex];
            props.onTabClose(tabToRemove);
        }

        if (props.redirectToTabAfterClose !== undefined) {
            goToTab(props.redirectToTabAfterClose);
        }
    };

    const isGuidTab = (tab: TabView): boolean => {
        return tab.key?.length === 36;
    };

    return (
        <>
            <NavigationPanelContainer
                id={tabsNavigationDOMId}
                key={`navigationPanelContainer`}
                tabs={props.tabs}
                activeTabIndex={activeTab}
                upperCaseTabs={props.upperCaseTabs}
                className={props.className}
                customContextMenuItems={props.customContextMenuItems}
                showContextMenu={props.showContextMenu}
                closeTabsHandler={props.closeTabsHandler}
                onTabSelected={(tabIndex: number) => goToTab(tabIndex)}
                onTabClose={(key: number) => onTabClose(key)}
                suppressDropdown={props.suppressDropdown}
                inModal
            />
            <TabContent activeTab={activeTab} className={props.tabContentClassName}>
                {props.tabs.map((tab, index) => (
                    <TabPane
                        tabId={index}
                        key={index}
                        className={props.tabPaneClassName || ''}
                        style={{
                            overflowY: tab.scrollPosition || 'auto',
                            scrollbarGutter: tab.scrollPosition === 'hidden' ? 'unset' : undefined,
                        }}
                    >
                        {props.renderOnlyActiveTab ? (
                            activeTab?.toString() === tab.key || isGuidTab(tab) ? (
                                tab.component
                            ) : (
                                <></>
                            )
                        ) : (
                            tab.component
                        )}
                    </TabPane>
                ))}
            </TabContent>
        </>
    );
}
